import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatError, MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { Preferences } from '../../_models/preferences';
import { UnivPrefsService } from '../../_services/univ-prefs.service';
import { COHORT_YEARS } from '../../_shared/aid-year/aid-year.config';

@Component({
  selector: 'app-cohort-phrase-card',
  templateUrl: './cohort-phrase-card.component.html',
  styleUrls: ['./cohort-phrase-card.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatButton,
    MatCard,
    MatCardContent,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    MatSuffix,
    MatOption,
    MatSelect
  ]
})
export class CohortPhraseCardComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly cohortYears = COHORT_YEARS;
  readonly cohortForm: UntypedFormGroup;
  saving = false;
  saveStatus = '';

  get legacyCohortYear(): string | null {
    const year = this.cohortForm.get('cohortAidPercentYear')?.value;
    return year && !this.cohortYears.includes(year) ? year : null;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private preferences: UnivPrefsService,
    private announcer: LiveAnnouncer
  ) {
    this.cohortForm = this.fb.group({
      cohortAidPercentYear: ['', Validators.required],
      cohortAidPercent: ['', [Validators.required, Validators.min(0), Validators.max(100)]]
    });
  }

  ngOnInit(): void {
    this.preferences.draftPrefs.pipe(
      filter((prefs): prefs is Preferences => !!prefs),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(prefs => {
      if (this.cohortForm.dirty && !this.saving) {
        return;
      }
      this.cohortForm.patchValue({
        cohortAidPercentYear: prefs.cohortAidPercentYear || '',
        cohortAidPercent: prefs.cohortAidPercent || ''
      }, { emitEvent: false });
      this.cohortForm.markAsPristine();
    });

    this.cohortForm.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      if (!this.saving) {
        this.saveStatus = '';
      }
    });
  }

  async save(): Promise<void> {
    if (this.cohortForm.invalid || !this.cohortForm.dirty || this.saving) {
      this.cohortForm.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.saveStatus = 'Saving grant aid disclosure...';

    try {
      await this.preferences.savePreferences({
        cohortAidPercentYear: this.cohortForm.value.cohortAidPercentYear,
        cohortAidPercent: this.cohortForm.value.cohortAidPercent
      }, true);
      this.cohortForm.markAsPristine();
      this.saveStatus = 'Grant aid disclosure saved.';
      this.announcer.announce('Grant aid disclosure saved.');
    } catch (error) {
      console.error('Failed to save grant aid disclosure', error);
      this.saveStatus = 'Unable to save the grant aid disclosure. Try again.';
      this.announcer.announce('Unable to save the grant aid disclosure. Try again.');
    } finally {
      this.saving = false;
    }
  }
}
